<style>
  /* DM Sans is NOT imported here: index.html already loads it as a <link> (a superset of these
     weights) so it starts downloading with the document instead of waiting for this stylesheet. */
  @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css');

  .article-page {
    font-family: var(--font-sans);
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
  }

  .related-blogs-section {
    order: 4;
  }

  /* Four related posts fill the four, two and one column tiers exactly, but leave a single orphan
     card on its own row at three. Drop the fourth there rather than ending the section ragged —
     the same trim the home page's insights strip uses at this width. */
  @media (min-width: 1025px) and (max-width: 1500px) {
    .related-blogs-section .sav-blog-grid > *:nth-child(n + 4) {
      display: none;
    }
  }

  /* Spacing only. These used to pin sizes (H2 34px, H3 30px, H4 20px, H5 22px) which was wrong
     twice over: the article body is injected with [innerHTML] so it never carries this component's
     encapsulation attribute and the sizes never reached a single CMS heading — the article is
     styled by the global `.sav-blog-prose` rules instead. What they DID reach was the headings
     written in this template, so "Leave a Comment" rendered at 30px directly under a 26px section
     heading. H5 was also set larger than H4, inverting the hierarchy wherever both appeared.
     Size/weight/line-height now come from the global scale, per the rule at the top of styles.scss. */
  .article-page .prose h2,
  .article-page .prose h3,
  .article-page .prose h4,
  .article-page .prose h5,
  .article-page .prose h6 {
    margin: 2rem 0 0.85rem;
  }

  .article-page .prose p {
    margin-bottom: 1.25rem;
    line-height: 1.75;
    color: #475569;
  }

  .article-page .prose .coupon-hero h3,
  .article-page .prose .coupon-hero p {
    color: #ffffff;
  }

  .article-page .prose .coupon-inline-title {
    margin-top: 0;
    margin-bottom: 5px;
  }

  .article-page .prose .coupon-inline-desc {
    margin: 0;
    line-height: 1.4;
  }

  /* Brand purple, not slate. The panel used to blend `--color-primary` into #0f172a, landing on a
     navy-purple that is nowhere in the palette — and it left the purple Subscribe button sitting on
     a near-purple background with barely anything between them. Now it ramps between the two real
     brand purples, and the radius matches the article's other cards instead of being 28px. */
  .article-page .prose .blog-newsletter-widget {
    margin: 2rem 0;
    border-radius: 18px;
    background: linear-gradient(135deg, var(--color-primary-deep) 0%, var(--color-primary) 100%);
    padding: 32px;
  }

  .article-page .prose .blog-newsletter-widget__inner {
    display: flex;
    flex-direction: column;
    gap: 22px;
  }

  /* Colour and spacing only — the size comes from the global scale. It was 30px here and 38px under
     900px, i.e. the heading grew as the screen shrank. */
  .article-page .prose .blog-newsletter-widget h3 {
    margin: 0 0 5px;
    color: #ffffff;
    max-width: none;
  }

  .article-page .prose .blog-newsletter-widget p {
    margin: 0;
    max-width: none;
    /* 68% sat under the 4.5:1 contrast line against the panel; 78% clears it. */
    color: rgb(255 255 255 / 78%);
    font-size: 14.5px;
    line-height: 1.5;
    font-weight: 500;
  }

  .article-page .prose .blog-newsletter-widget__form {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    width: 100%;
  }

  /* The fused pill: a solid white shell holding the field and the action. The field used to be a
     translucent purple-on-purple box that was hard to see you could even type in. */
  .blog-newsletter-widget__field {
    display: flex;
    align-items: stretch;
    width: 100%;
    overflow: hidden;
    border-radius: 999px;
    background: #ffffff;
  }

  .article-page .prose .blog-newsletter-widget__form input {
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    background: transparent;
    color: var(--color-neutral-900);
    font-size: 15px;
    line-height: 1.4;
    padding: 0 22px;
    min-height: 56px;
    outline: none;
  }

  .article-page .prose .blog-newsletter-widget__form input::placeholder {
    color: #8b82a3;
  }

  .article-page .prose .blog-newsletter-widget__form button {
    flex: 0 0 auto;
    margin: 6px;
    border: 0;
    border-radius: 999px;
    background: var(--color-primary);
    color: #ffffff;
    font-size: 15px;
    line-height: 1;
    font-weight: 800;
    letter-spacing: 0.02em;
    white-space: nowrap;
    padding: 0 26px;
    min-height: 44px;
    cursor: pointer;
    transition: background-color 0.18s ease;
  }

  .article-page .prose .blog-newsletter-widget__form button:hover:not(:disabled),
  .article-page .prose .blog-newsletter-widget__form button:focus-visible {
    background: var(--color-primary-deep);
  }

  .article-page .prose .blog-newsletter-widget__form button:disabled {
    opacity: 0.75;
    cursor: default;
  }

  /* Both states were completely unstyled — the error inherited the muted body colour and vanished
     against the panel. Selectors carry the block class twice so they outrank the generic
     `.blog-newsletter-widget p` rule above. */
  .article-page .prose .blog-newsletter-widget .blog-newsletter-widget__error {
    padding-left: 22px;
    color: #ffd9d4;
    font-size: 13px;
    line-height: 1.4;
    font-weight: 600;
  }

  .article-page .prose .blog-newsletter-widget .blog-newsletter-widget__done {
    display: flex;
    align-items: center;
    min-height: 56px;
    padding: 0 24px;
    border-radius: 999px;
    border: 1px solid rgb(255 255 255 / 28%);
    background: rgb(255 255 255 / 14%);
    color: #ffffff;
    font-size: 15px;
    line-height: 1.4;
    font-weight: 600;
  }

  /* Below this the pill has no room for a field and a "Subscribe Now" side by side. */
  @media (max-width: 560px) {
    .blog-newsletter-widget__field {
      flex-direction: column;
      border-radius: 22px;
      padding: 6px;
    }

    .article-page .prose .blog-newsletter-widget__form input {
      min-height: 48px;
      padding: 0 16px;
    }

    .article-page .prose .blog-newsletter-widget__form button {
      margin: 0;
      min-height: 48px;
    }
  }

  @media (max-width: 900px) {
    .article-page .prose .blog-newsletter-widget {
      padding: 24px;
    }
  }

  .article-page .sticky nav a,
  .article-page .sticky nav a:focus,
  .article-page .sticky nav a:focus-visible {
    outline: none;
    box-shadow: none;
  }

  .article-page .toc-scroll-target {
    scroll-margin-top: 200px;
  }

  .article-page .prose pre {
    margin: 1.25rem 0;
    border: 1px solid #1e293b;
    border-radius: 1rem;
    background: #0f172a;
    padding: 1rem 1.25rem;
    overflow-x: auto;
  }

  .article-page .prose pre code {
    background: transparent;
    color: #e2e8f0;
    font-family:
      'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 16px;
    line-height: 1.7;
    font-weight: 500;
    text-shadow: none;
    padding: 0;
  }

  .article-page .prose .table-wrap {
    margin: 1.5rem 0;
    overflow-x: auto;
    border-radius: 1rem;
    border: 1px solid #e2e8f0;
    background: #ffffff;
  }

  .article-page .prose table {
    width: 100%;
    margin: 0;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 620px;
  }

  .article-page .prose thead th {
    border-bottom: 1px solid #e2e8f0;
    background: var(--color-primary);
    color: #ffffff;
    font-family: var(--font-heading);
    font-size: 14px;
    line-height: 1.4;
    font-weight: 700;
    letter-spacing: 0.01em;
    padding: 14px 16px;
    text-align: left;
  }

  .article-page .prose tbody td {
    border-bottom: 1px solid #eef2f7;
    color: #334155;
    font-size: 14px;
    line-height: 1.5;
    font-weight: 400;
    padding: 14px 16px;
    vertical-align: top;
  }

  .article-page .prose tbody tr:last-child td {
    border-bottom: 0;
  }

  .article-page .prose tbody tr:nth-child(even) td {
    background: #fcfdff;
  }

  .article-page .prose dl {
    margin: 1.5rem 0;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    background: #ffffff;
    padding: 1rem 1.25rem;
  }

  .article-page .prose dt {
    margin: 0;
    color: #1e293b;
    font-size: 20px;
    line-height: 1.3;
    font-weight: 700;
  }

  .article-page .prose dt:not(:first-child) {
    margin-top: 0.9rem;
    padding-top: 0.9rem;
    border-top: 1px dashed #e2e8f0;
  }

  .article-page .prose dd {
    margin: 0.35rem 0 0;
    color: #475569;
    font-size: 16px;
    line-height: 1.6;
    font-weight: 500;
  }

  /* The author card is the shared `.sav-side-card` now, so its chrome, author row, avatar, bio and
     "Read more" link all come from the global rules the store page's editor card uses. Only the
     gap to the sticky TOC below it is local. */
  .sav-side-card {
    margin-bottom: 1.5rem;
  }

  /* This page runs flat — no shadows anywhere. The shared card carries one from styles.scss, so it
     is switched off here rather than globally: the store page still uses it. */
  .sav-side-card,
  .sav-side-card:hover,
  .sav-side-card-updated {
    box-shadow: none;
  }

  /* Same for the coupon and product cards dropped into the article as widgets — they ship their own
     shadows and are used across the site, so they are flattened here rather than at source.
     `::ng-deep` is required: those elements belong to child components and never carry this one's
     encapsulation attribute, so a plain descendant selector would not reach them. */
  .article-page ::ng-deep .sav-blog-widget [class*='shadow'],
  .article-page ::ng-deep .sav-store-offer-card,
  .article-page ::ng-deep .sav-store-offer-card [class*='shadow'],
  /* The shared event top-stores row keeps its lifted logo tiles elsewhere; this page runs flat. */
  .article-page ::ng-deep .sav-top-store {
    box-shadow: none;
  }

  .blog-cast-strip {
    margin: 0 0 50px;
    order: 3;
    background: #ffffff;
    padding: 48px 0;
  }

  .blog-cast-container {
    width: min(var(--site-container), 100%);
    margin: 0 auto;
    padding: 0 var(--container-inline-pad);
    box-sizing: border-box;
  }

  .blog-cast-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin: 0 0 24px;
  }

  /* Was 34px (26px under 720px) — the largest heading on a page whose other section headings are
     26px, and larger than the article's own H1. Size now comes from the global scale. */
  .blog-cast-container h2 {
    margin: 0;
  }

  .blog-cast-nav {
    display: flex;
    gap: 10px;
    flex-shrink: 0;
  }

  /* Reuses the site's carousel arrow, but anchored beside the heading rather than over the track. */
  .blog-cast-arrow {
    position: static;
    transform: none;
  }

  .blog-cast-arrow:disabled {
    opacity: 0.4;
    cursor: default;
    box-shadow: none;
  }

  /* Native scrolling: swipeable on touch, keyboard-scrollable, and it still renders every author
     server-side (no JS needed for the content to exist). */
  .blog-cast-row {
    display: flex;
    gap: 22px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-bottom: 4px;
  }

  .blog-cast-row::-webkit-scrollbar {
    display: none;
  }

  .blog-cast-link {
    display: block;
    text-align: center;
    text-decoration: none;
    flex: 0 0 calc((100% - 7 * 22px) / 8);
    scroll-snap-align: start;
  }

  .blog-cast-photo {
    width: 120px;
    height: 120px;
    margin: 0 auto 0.625rem;
    border: 2px solid #334155;
    border-radius: 999px;
    background: #1e293b;
    object-fit: cover;
    display: block;
    transition: border-color 0.2s ease;
  }

  .blog-cast-initial {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-family: var(--font-heading);
    font-size: 40px;
    font-weight: 700;
  }

  .blog-cast-link:hover .blog-cast-photo,
  .blog-cast-link:focus-visible .blog-cast-photo {
    border-color: var(--color-primary);
  }

  .blog-cast-name {
    margin: 0;
    color: #000000;
    font-family: var(--font-heading);
    font-size: 18px;
    line-height: 1.35;
    font-weight: 600;
  }

  .blog-cast-role {
    margin: 2px 0 0;
    color: #333333;
    font-size: 14px;
    line-height: 1.35;
  }

  .zaful-competitors {
    margin-bottom: 50px;
    margin-top: 0;
    padding: 0;
    order: 3;
  }

  @media (max-width: 1100px) {
    .blog-cast-link {
      flex-basis: calc((100% - 3 * 22px) / 4);
    }
  }

  @media (max-width: 720px) {
    .blog-cast-strip {
      padding: 34px 0;
    }

    .blog-cast-row {
      gap: 12px;
    }

    .blog-cast-link {
      flex-basis: calc((100% - 12px) / 2);
    }
  }

  /* Was 30px (28px under 768px) — a third distinct H2 size on the same page. Size now comes from
     the global scale, so it matches the section headings above and below it. */
  .zaful-competitors h2 {
    margin: 0 0 22px;
  }

  .zaful-competitor-grid {
    margin-top: 30px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 30px 36px;
  }

  .zaful-competitor-card {
    display: grid;
    grid-template-columns: 132px minmax(0, 1fr);
    align-items: start;
    gap: 18px;
    /* The whole card is the link to the store page, so it must not inherit link styling. */
    text-decoration: none;
    color: inherit;
  }

  .zaful-competitor-card:hover .zaful-competitor-content h3,
  .zaful-competitor-card:focus-visible .zaful-competitor-content h3 {
    color: var(--color-primary);
  }

  .zaful-competitor-card > img {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    border: 1px solid #e5e7eb;
    object-fit: cover;
    display: block;
  }

  /* Layout and spacing only; size comes from the global scale. */
  .zaful-competitor-content h3 {
    margin: 4px 0 6px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .zaful-competitor-content > p {
    margin: 0;
    color: #1f2937;
    font-family: var(--font-sans);
    font-size: 14px;
    line-height: 1.45;
    font-weight: 400;
  }

  .zaful-competitor-domain {
    margin: 0 0 14px;
    color: #6b7280;
    font-family: var(--font-sans);
    font-size: 18px;
    line-height: 1.2;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    /* Was still two columns here. With a 96px logo plus gaps inside a ~150px column, the store name
       and description were left roughly 40px of width on a 390px phone. One card per row below the
       tablet breakpoint. */
    .zaful-competitor-grid {
      grid-template-columns: minmax(0, 1fr);
      gap: 22px;
    }

    .zaful-competitor-card {
      grid-template-columns: 96px minmax(0, 1fr);
      gap: 14px;
    }

    .zaful-competitor-card > img {
      width: 88px;
      height: 88px;
    }

    .zaful-competitor-content h3 {
      margin-top: 0;
    }

    .zaful-competitor-content > p {
      font-size: 16px;
    }
  }
  /* ---------- In-content widgets ---------- */

  /* Blocks injected between article paragraphs. They interrupt the prose deliberately, so they get
     clear breathing room rather than reading as part of the surrounding text. */
  .sav-blog-widget {
    display: block;
    margin: 32px 0;
  }

  /* A short admin-authored lead-in above a coupon/product card. */
  .article-page .prose .sav-blog-widget-lead {
    margin: 0 0 12px;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--color-primary);
  }

  .sav-blog-widget-store {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 18px 20px;
    border: 1px solid #ead9f4;
    border-radius: 16px;
    background: #ffffff;
    text-decoration: none;
    transition: border-color 160ms ease;
  }

  .sav-blog-widget-store:hover,
  .sav-blog-widget-store:focus-visible {
    border-color: var(--color-primary);
  }

  .sav-blog-widget-store-logo {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    overflow: hidden;
    border-radius: 12px;
    background: #f7f8fa;
  }

  .sav-blog-widget-store-logo img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .sav-blog-widget-store-text {
    flex: 1 1 auto;
    min-width: 0;
  }

  .sav-blog-widget-store-name {
    display: block;
    font-family: var(--font-heading);
    font-size: 16px;
    font-weight: 700;
    color: var(--color-neutral-900);
  }

  .sav-blog-widget-store-desc {
    display: block;
    margin-top: 4px;
    font-size: 13.5px;
    line-height: 1.5;
    color: #6e6684;
  }

  .sav-blog-widget-store-cue {
    flex-shrink: 0;
    font-family: var(--font-sans);
    font-size: 12.5px;
    font-weight: 700;
    white-space: nowrap;
    color: var(--color-primary);
  }

  /* Callout: a quiet aside. CTA: the same box in the theme tint, so it reads as an ask. */
  .sav-blog-widget-callout,
  .sav-blog-widget-cta {
    padding: 20px 22px;
    border-radius: 16px;
  }

  .sav-blog-widget-callout {
    border: 1px solid #ead9f4;
    border-left: 4px solid var(--color-primary);
    background: #ffffff;
  }

  /* The CTA is the article's one "do something next" block, so it carries more weight than the
     callout. It was a flat tinted rectangle with the button stranded under the copy at the bottom
     left; it now has a defined edge, a soft lift and real padding, and on a column wide enough for
     it the copy and the action sit on one line instead of stacking. */
  .sav-blog-widget-cta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 18px 28px;
    padding: 26px 28px;
    border: 1px solid #e0c8f0;
    border-radius: 18px;
    background: linear-gradient(135deg, var(--color-primary-tint) 0%, #faf5fd 100%);
  }

  .sav-blog-widget-text {
    min-width: 0;
  }

  /* Wraps to its own row below ~320px of copy rather than squeezing the button off the edge. */
  .sav-blog-widget-cta .sav-blog-widget-text {
    flex: 1 1 320px;
  }

  .article-page .prose .sav-blog-widget-title {
    margin: 0;
    font-family: var(--font-heading);
    font-size: 17px;
    font-weight: 700;
    color: var(--color-neutral-900);
  }

  .article-page .prose .sav-blog-widget-body {
    margin: 8px 0 0;
    font-size: 14.5px;
    line-height: 1.7;
    color: #4b4459;
  }

  .sav-blog-widget-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 46px;
    margin-top: 16px;
    padding: 0 24px;
    border-radius: 12px;
    background: var(--color-primary);
    color: #ffffff;
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    text-decoration: none;
    transition: background-color 0.18s ease;
  }

  .sav-blog-widget-btn svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  /* In the CTA the button is a column beside the copy, not a block stacked under it. */
  .sav-blog-widget-cta .sav-blog-widget-btn {
    margin-top: 0;
    flex-shrink: 0;
  }

  .sav-blog-widget-btn:hover,
  .sav-blog-widget-btn:focus-visible {
    background: var(--color-primary-deep);
  }
  /* ---------- Article layout ---------- */

  /* One sticky rail plus a proper reading measure. The old three-column split left the article at
     632px on a 1600px page, with a whole rail given to stores unrelated to the article. */
  /* Mirrors the store page's `.sav-top-wrap` exactly — same 1fr/3fr split, same fluid gap, same
     1200px collapse — so the article sidebar is the same width as the store sidebar. Both pages sit
     in a 7.5%-gutter container, so equal proportions give equal pixels.
     This also settles the old rail: it was a flat 300px against a fluid container, so the article
     column grew with the viewport while the article inside it stayed pinned to 780px, leaving a
     ~500px void down its right at 1920px. Proportional columns keep the two in step at any width. */
  .sav-article-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 3fr);
    gap: clamp(28px, 4vw, 56px);
    align-items: start;
    /* Breathing room under the hero. This was a flat `py-12` (48px), which reads as almost nothing
       on a wide screen where the hero is a full-bleed dark band — the author card and the first
       paragraph both started right against it. Scales with the viewport like the column gap does. */
    padding-block: clamp(44px, 4.5vw, 80px);
  }

  /* The rail is a full-height column, NOT a sticky box.
     It used to be `position: sticky; top: 24px`, which broke the sidebar two ways at once:
       1. 24px is measured from the viewport, but the site header is sticky and 128px tall (86px
          brand row + 42px nav row), so the author card parked ~104px underneath it.
       2. A sticky ancestor traps a sticky descendant — a child can only travel inside its parent's
          box — so the "On This Page" panel nested inside it could never stick at all.
     Stretching the rail to the row height instead gives that panel the full length of the article
     to travel down, which is what the sidebar was supposed to do. */
  .sav-article-rail {
    align-self: stretch;
  }

  /* Parks just below the sticky site header (128px) with breathing room. */
  .sav-article-toc {
    position: sticky;
    top: 170px;
  }

  /* The header compacts to 100px between 1280px and 1536px (see main-layout.html); without this the
     panel parked 28px lower than the same gap everywhere else. */
  @media (min-width: 1280px) and (max-width: 1535.98px) {
    .sav-article-toc {
      top: 142px;
    }
  }

  /* h2 for the outline: the rail is the first thing after the page H1, so an h3 here skipped a
     level. The size stays the h3 it has always been — this titles a widget, not a section. */
  .sav-article-toc h2 {
    font-size: var(--h3-size);
    font-weight: var(--h3-weight);
    line-height: var(--h3-line);
    letter-spacing: var(--h3-tracking);
  }

  /* Fills its column, like the store page's main column does. `min-width: 0` keeps a wide child —
     a table, a code block — from pushing the grid track open instead of scrolling inside itself. */
  .sav-article-body {
    width: 100%;
    min-width: 0;
  }

  /* 1200px, not 1023px — the same width the store page stacks its sidebar at. */
  @media (max-width: 1200px) {
    /* Article first, rail after.
       The rail is the first column in the markup, so collapsing the grid put the author card and
       the table of contents above the article — a reader arriving on a post met the writer's bio
       before a word of what they came to read. A flex column swaps the two without touching the
       markup, which the desktop layout still needs in its original order. */
    .sav-article-layout {
      display: flex;
      flex-direction: column;
      gap: 32px;
    }

    .sav-article-body {
      order: 1;
    }

    .sav-article-rail {
      order: 2;
    }

    /* Stacked above the article rather than beside it, so nothing in the rail should stick —
       sticky here would strand the panel mid-scroll. */
    .sav-article-toc {
      position: static;
    }
  }

  /* ---------- Article lists ---------- */

  /* Tailwind's preflight strips list markers globally, so admin-authored lists arrived with no
     bullets AND no spacing — a bulleted list read as one run-on paragraph. */
  .article-page .prose ul,
  .article-page .prose ol {
    margin: 0 0 1.25rem;
    padding-left: 1.4rem;
    color: #475569;
  }

  .article-page .prose ul {
    list-style: disc;
  }

  .article-page .prose ol {
    list-style: decimal;
  }

  .article-page .prose li {
    margin-bottom: 0.5rem;
    line-height: 1.75;
  }

  .article-page .prose li::marker {
    color: var(--color-primary);
  }

  .article-page .prose blockquote {
    margin: 0 0 1.25rem;
    padding: 4px 0 4px 18px;
    border-left: 3px solid var(--color-primary);
    color: #3f3a4d;
    font-style: italic;
  }

  /* ---------- Sidebar author card ---------- */

  /* The card's label size now comes from the shared `.sav-side-card h3` rule in styles.scss — this
     page no longer states any of the card's type. */
  .sav-author-updated {
    margin-top: 12px;
    font-size: 12.5px;
    color: #8b82a3;
  }
  /* ---------- Comments ----------
     Deliberately plain: a bordered card, an initials circle and two type sizes. The old markup ran
     on Tailwind's blue-600 (nowhere in this palette) and pulled an avatar image per comment from
     ui-avatars.com. */

  /* Bubble layout: avatar beside a tinted bubble, with a small meta row under it — the compact
     shape social feeds use. The bordered white card this replaced spent ~34px of vertical padding
     and a 14px gap on every comment before a word of text. */
  .sav-comment-list,
  .sav-comment-replies {
    list-style: none;
    margin: 0 0 20px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  /* Indented to line up under the parent's bubble (32px avatar + 10px gap). */
  .sav-comment-replies {
    margin: 8px 0 0;
    padding-left: 42px;
    gap: 8px;
  }

  .sav-comment {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .sav-comment-main {
    flex: 1 1 auto;
    min-width: 0;
  }

  .sav-comment-bubble {
    border-radius: 14px;
    background: #f5f2f8;
    padding: 8px 13px 10px;
  }

  .sav-comment-avatar {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--color-primary-tint);
    color: var(--color-primary-deep);
    font-family: var(--font-heading);
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
  }

  .sav-comment-replies .sav-comment-avatar {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }

  .article-page .prose .sav-comment-author {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 7px;
    color: var(--color-neutral-900);
    font-family: var(--font-heading);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
  }

  .sav-comment-badge {
    border-radius: 999px;
    background: var(--color-primary);
    color: #ffffff;
    font-family: var(--font-sans);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 2px 6px;
  }

  .article-page .prose .sav-comment-body {
    margin: 2px 0 0;
    color: #3f3a4d;
    font-size: 14px;
    line-height: 1.5;
    /* Reader text: a pasted run of characters must not push the bubble open. */
    overflow-wrap: anywhere;
  }

  /* Tiny action row under the bubble, indented to the bubble's text. */
  .sav-comment-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 3px;
    padding-left: 13px;
    color: #8b82a3;
    font-size: 12px;
  }

  .sav-comment-link {
    border: 0;
    background: transparent;
    padding: 0;
    color: #6e6684;
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }

  .sav-comment-link:hover {
    color: var(--color-primary);
    text-decoration: underline;
  }

  .article-page .prose .sav-comment-empty {
    margin: 0 0 16px;
    color: #6e6684;
    font-size: 14px;
  }

  /* ---------- Comment form ---------- */

  .sav-comment-form {
    display: flex;
    flex-direction: column;
    gap: 8px;
    border: 1px solid #e6e0ee;
    border-radius: 14px;
    background: #ffffff;
    padding: 14px;
  }

  /* Sits inside the comment's own column, so it needs no extra indent of its own. */
  .sav-comment-form--reply {
    margin-top: 8px;
    padding: 12px;
    background: #fcfaff;
  }

  .article-page .prose .sav-comment-form-title {
    margin: 0;
    color: var(--color-neutral-900);
    font-family: var(--font-heading);
    font-size: 14px;
    font-weight: 700;
  }

  .sav-comment-form input,
  .sav-comment-form textarea {
    width: 100%;
    border: 1px solid #e2ddea;
    border-radius: 10px;
    background: #ffffff;
    color: var(--color-neutral-900);
    font-family: var(--font-sans);
    font-size: 14px;
    line-height: 1.45;
    padding: 9px 12px;
    outline: none;
    resize: vertical;
  }

  .sav-comment-form input:focus,
  .sav-comment-form textarea:focus {
    border-color: var(--color-primary);
  }

  .sav-comment-form input::placeholder,
  .sav-comment-form textarea::placeholder {
    color: #a49cb8;
  }

  .sav-comment-submit {
    align-self: flex-start;
    border: 0;
    border-radius: 9px;
    background: var(--color-primary);
    color: #ffffff;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 700;
    min-height: 36px;
    padding: 0 18px;
    cursor: pointer;
    transition: background-color 0.18s ease;
  }

  .sav-comment-submit:hover:not(:disabled) {
    background: var(--color-primary-deep);
  }

  .sav-comment-submit:disabled {
    opacity: 0.7;
    cursor: default;
  }

  .article-page .prose .sav-comment-error,
  .article-page .prose .sav-comment-queued {
    margin: 0;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.5;
    font-weight: 600;
    padding: 10px 14px;
  }

  .article-page .prose .sav-comment-error {
    background: #fdeceb;
    color: #c0392b;
  }

  .article-page .prose .sav-comment-queued {
    background: var(--color-primary-tint);
    color: var(--color-primary-deep);
  }

  .article-page .prose .sav-comment-note {
    margin: 0;
    color: #8b82a3;
    font-size: 12.5px;
  }

  /* A single product card keeps its grid width instead of stretching the reading column, and sits
     centred in it — left-aligned it read as a stray card floating beside the text. The lead label
     rides with it, so the two stay aligned as one block. */
  .sav-blog-widget-product {
    max-width: 320px;
    margin-inline: auto;
  }

  /* Several product cards at one spot: a grid row, three across in the reading column, two on a
     tablet, two on a phone (the card is built for a 2-up phone grid elsewhere on the site). */
  .sav-blog-widget-products {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    align-items: start;
  }

  .sav-blog-widget-products-item {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /* Labels run to two lines at most in a grid cell, so the cards below them stay level. */
  .article-page .prose .sav-blog-widget-products .sav-blog-widget-lead {
    min-height: 2.6em;
    margin-bottom: 8px;
    font-size: 12px;
    line-height: 1.3;
  }

  @media (max-width: 900px) {
    .sav-blog-widget-products {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }
  }
</style>

<div class="article-page antialiased">
  <!-- min-h rather than a fixed h-[400px]: H1 is pinned at 34px on every viewport, so a long title
       on a narrow screen ran past 400px and `overflow-hidden` cropped the byline off the bottom.
       The band now grows with its content and still fills 400px for short titles. -->
  <section class="relative flex min-h-[400px] items-center overflow-hidden bg-black py-12 md:py-0">
    @if (thumbnailUrl()) {
      <img
        [src]="thumbnailUrl() | assetUrl"
        [alt]="title()"
        class="absolute inset-0 h-full w-full object-cover opacity-60"
      />
    }
    <div class="sav-site-container relative z-10">
      <!-- On the dark hero band, hence the variant. This used to be a hand-written nav with no
           `aria-label`, so assistive tech did not announce it as a breadcrumb at all. -->
      <app-breadcrumb [items]="crumbs()" variant="on-dark" />
      <!-- No `max-w-4xl`: the cap held the title to 896px while the band around it runs the full
           width, so a two-line title broke mid-phrase with the right of the hero left empty. It now
           uses the same container width as the breadcrumb and byline under it. -->
      <h1 class="type-on-dark">
        {{ title() }}
      </h1>
      <div class="mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div class="flex items-center gap-4">
          <div
            class="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-primary)] font-bold text-white"
          >
            {{ initials(authorName()) }}
          </div>
          <div class="text-white">
            <p class="font-semibold">{{ authorName() }}</p>
            <p class="text-sm text-gray-300">
              @if (updatedLabel()) {
                Updated {{ updatedLabel() }} •
              } @else if (publishedLabel()) {
                {{ publishedLabel() }} •
              }
              {{ readTime() }} min read
            </p>
          </div>
        </div>

        @if (viewsLabel()) {
          <div
            class="flex flex-wrap items-center gap-4 rounded-full bg-black/35 px-4 py-2 text-sm text-white backdrop-blur-sm md:justify-end"
          >
            <span>{{ viewsLabel() }}</span>
          </div>
        }
      </div>
    </div>
  </section>

  <!-- Vertical padding lives in `.sav-article-layout` (fluid), not a fixed `py-12` here. -->
  <main class="sav-site-container sav-article-layout">
    <aside class="sav-article-rail">
      <!-- The article's real author. Was a hardcoded card (a fictional "Shannon Flynn", an
           external image URL the browser blocked cross-origin, and a dead "See Bio" link). The
           avatar is generated from the name, since the blog payload carries no author image. -->
      @if (articleAuthor(); as author) {
        <article class="sav-side-card sav-side-card-updated">
          <h2>Reviewed by</h2>
          <div class="sav-author-row">
            @if (author.avatarPath) {
              <img
                class="sav-author-avatar"
                [src]="author.avatarPath | assetUrl"
                [alt]="author.name"
                width="96"
                height="96"
              />
            } @else {
              <span class="sav-author-avatar sav-author-avatar-initials" aria-hidden="true">{{
                author.initials
              }}</span>
            }
            <div class="sav-author-meta">
              <p class="sav-author-name">{{ author.name }}</p>
              @if (author.role) {
                <p class="sav-author-role">{{ author.role }}</p>
              }
            </div>
          </div>
          @if (author.bio) {
            <p class="sav-author-bio">{{ author.bio }}</p>
          }
          @if (author.profileUrl) {
            <a class="sav-author-bio-link" [routerLink]="author.profileUrl"
              >Read more
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                ></path>
              </svg>
            </a>
          }
          @if (updatedLabel()) {
            <p class="sav-author-updated">Last updated {{ updatedLabel() }}</p>
          }
        </article>
      }

      <div class="sav-article-toc rounded-2xl border border-gray-200 bg-white p-6">
        <h2 class="mb-4 flex items-center">
          <i class="fa-solid fa-list-ul mr-2 text-[var(--color-primary)]"></i> On This Page
        </h2>
        <nav class="space-y-4 text-sm">
          @for (group of tocGroups(); track group.id; let isFirst = $first) {
            <div>
              <a
                [href]="'#' + group.id"
                [class]="
                  activeTocId() === group.id
                    ? 'block font-bold text-[var(--color-primary)] no-underline outline-none'
                    : 'block font-bold text-gray-700 outline-none hover:text-[var(--color-primary)]'
                "
                (click)="scrollToHeading($event, group.id)"
              >
                {{ group.text }}
              </a>

              @if (group.children.length > 0) {
                <div class="mt-2 space-y-2 pl-4">
                  @for (child of group.children; track child.id) {
                    <a
                      [href]="'#' + child.id"
                      [class]="
                        activeTocId() === child.id
                          ? 'block text-[13px] font-medium text-[var(--color-primary)] outline-none'
                          : 'block text-[13px] font-medium text-gray-500 outline-none transition hover:text-[var(--color-primary)]'
                      "
                      (click)="scrollToHeading($event, child.id)"
                    >
                      {{ child.text }}
                    </a>
                  }
                </div>
              }
            </div>
          }
        </nav>

        <div class="mt-8 border-t border-gray-100 pt-6">
          <p class="mb-3 text-xs font-bold uppercase tracking-widest text-gray-400">Share Guide</p>
          <div class="flex gap-3">
            <button
              class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 transition hover:bg-blue-600 hover:text-white"
            >
              <i class="fa-brands fa-facebook-f"></i>
            </button>
            <button
              class="flex h-10 w-10 items-center justify-center rounded-full bg-sky-100 text-sky-500 transition hover:bg-sky-500 hover:text-white"
            >
              <i class="fa-brands fa-twitter"></i>
            </button>
            <button
              class="flex h-10 w-10 items-center justify-center rounded-full bg-pink-100 text-pink-600 transition hover:bg-pink-600 hover:text-white"
            >
              <i class="fa-brands fa-pinterest"></i>
            </button>
          </div>
        </div>
      </div>
    </aside>

    <article class="prose prose-slate sav-article-body">
      <!-- Article body: admin-authored CMS HTML from the live blog API, split into top-level blocks
           so admin-placed widgets can be rendered as REAL components between them (a coupon's reveal
           flow cannot work inside an innerHTML string). Heading ids are injected in the same pass,
           so the "On This Page" TOC still anchors correctly. -->
      @if (contentBlocks().length) {
        <div class="sav-blog-article">
          @for (block of contentBlocks(); track block.id) {
            @if (block.kind === 'html') {
              <div class="sav-blog-prose" [innerHTML]="block.html | trustedCmsHtml"></div>
            } @else if (block.kind === 'products') {
              <!-- Back-to-back product widgets share one responsive grid row; each keeps its own
                   lead label above its card. Same card bindings as the single product widget below. -->
              <div class="sav-blog-widget sav-blog-widget-products">
                @for (item of block.widgets; track item.id) {
                  @if (item.product; as product) {
                    <div class="sav-blog-widget-products-item">
                      @if (item.title) {
                        <p class="sav-blog-widget-lead">{{ item.title }}</p>
                      }
                      <app-product-card
                        [title]="product.title || product.name"
                        [image]="product.thumbnailImagePath || '' | assetUrl"
                        [hoverImage]="product.galleryImagePaths?.[0] || '' | assetUrl"
                        [storeName]="product.store?.name || ''"
                        [storeLogoUrl]="product.store?.logoImagePath || '' | assetUrl"
                        [price]="product.newPrice || ''"
                        [oldPrice]="product.oldPrice || ''"
                        [external]="product.isExternal === true"
                        [detailHref]="product.slug ? '/' + product.slug : ''"
                        [productId]="product.id"
                      />
                    </div>
                  }
                }
              </div>
            } @else {
              @switch (block.widget.type) {
                @case ('coupon') {
                  @if (couponCardView(block.widget); as card) {
                    <div class="sav-blog-widget sav-blog-widget-coupon">
                      @if (block.widget.title) {
                        <p class="sav-blog-widget-lead">{{ block.widget.title }}</p>
                      }
                      <app-coupon-card
                        [card]="card"
                        (action)="onWidgetCouponAction(block.widget)"
                      />
                    </div>
                  }
                }

                @case ('store') {
                  @if (block.widget.store; as store) {
                    <a
                      class="sav-blog-widget sav-blog-widget-store"
                      [routerLink]="['/', store.slug]"
                    >
                      <span class="sav-blog-widget-store-logo">
                        @if (store.logoImagePath) {
                          <img
                            [src]="store.logoImagePath | assetUrl"
                            [alt]="store.name + ' logo'"
                            loading="lazy"
                          />
                        }
                      </span>
                      <span class="sav-blog-widget-store-text">
                        <span class="sav-blog-widget-store-name">{{ store.name }}</span>
                        @if (block.widget.body) {
                          <span class="sav-blog-widget-store-desc">{{ block.widget.body }}</span>
                        }
                      </span>
                      <span class="sav-blog-widget-store-cue" aria-hidden="true"
                        >View offers →</span
                      >
                    </a>
                  }
                }

                @case ('product') {
                  @if (block.widget.product; as product) {
                    <div class="sav-blog-widget sav-blog-widget-product">
                      @if (block.widget.title) {
                        <p class="sav-blog-widget-lead">{{ block.widget.title }}</p>
                      }
                      <!-- Bound the same way as the product grid and the category page, so the
                           article's card is the full card: both actions ("Buy Now" through the
                           /go/p/:id redirect, plus "View Detail"), and the store logo badge.
                           It only rendered one button here because `href` was the in-app detail
                           path — the card derives a buy link from an OFF-SITE href, so an internal
                           one left it with nothing to buy from. -->
                      <app-product-card
                        [title]="product.title || product.name"
                        [image]="product.thumbnailImagePath || '' | assetUrl"
                        [hoverImage]="product.galleryImagePaths?.[0] || '' | assetUrl"
                        [storeName]="product.store?.name || ''"
                        [storeLogoUrl]="product.store?.logoImagePath || '' | assetUrl"
                        [price]="product.newPrice || ''"
                        [oldPrice]="product.oldPrice || ''"
                        [external]="product.isExternal === true"
                        [detailHref]="product.slug ? '/' + product.slug : ''"
                        [productId]="product.id"
                      />
                    </div>
                  }
                }

                <!-- callout / cta: admin-authored copy, no reference to resolve. -->
                @default {
                  <aside
                    class="sav-blog-widget"
                    [class.sav-blog-widget-callout]="block.widget.type === 'callout'"
                    [class.sav-blog-widget-cta]="block.widget.type === 'cta'"
                  >
                    <!-- Copy is wrapped so the CTA can sit it beside the button on one line; the
                         callout keeps it stacked. -->
                    <div class="sav-blog-widget-text">
                      @if (block.widget.title) {
                        <p class="sav-blog-widget-title">{{ block.widget.title }}</p>
                      }
                      @if (block.widget.body) {
                        <p class="sav-blog-widget-body">{{ block.widget.body }}</p>
                      }
                    </div>
                    <!-- The URL is admin-entered, so an in-app path routes client-side and an
                         off-site one opens in a new tab. This was always a plain `href` carrying
                         `rel="nofollow sponsored"`, which reloaded the whole app on internal links
                         like /coupons AND told search engines the site's own pages were paid. -->
                    @if (block.widget.buttonLabel && block.widget.buttonUrl) {
                      @if (isExternalUrl(block.widget.buttonUrl)) {
                        <a
                          class="sav-blog-widget-btn"
                          [href]="block.widget.buttonUrl"
                          target="_blank"
                          rel="nofollow sponsored noopener"
                        >
                          {{ block.widget.buttonLabel }}
                          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path
                              d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                            ></path>
                          </svg>
                        </a>
                      } @else {
                        <a class="sav-blog-widget-btn" [routerLink]="block.widget.buttonUrl">
                          {{ block.widget.buttonLabel }}
                          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path
                              d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                            ></path>
                          </svg>
                        </a>
                      }
                    }
                  </aside>
                }
              }
            }
          }
        </div>
      } @else if (isLoading()) {
        <p class="text-slate-500">Loading article…</p>
      }

      @if (faqItems().length) {
        <div id="faqs">
          <app-section-heading
            heading="Frequently Asked Questions"
            subtitle="Quick answers to what readers ask about this topic."
          />
        </div>
        <!-- Shared FAQ accordion (`.sav-store-faq-*` — the <details> shape of the same design), so
             article FAQs match the ones on store, category and campaign pages. -->
        <div class="not-prose sav-store-faq-list mb-12">
          @for (faq of faqItems(); track faq.question) {
            <details class="sav-store-faq-item">
              <summary>{{ faq.question }}</summary>
              <div class="sav-faq-answer-body mt-3" [innerHTML]="faq.answer | trustedCmsHtml"></div>
            </details>
          }
        </div>
      }

      <div class="not-prose blog-newsletter-widget" aria-label="Newsletter signup callout">
        <div class="blog-newsletter-widget__inner">
          <div>
            <h3>Never Miss a Deal!</h3>
            <p>Join 50,000+ shoppers and get the best coupon codes delivered weekly.</p>
          </div>
          @if (signup.subscribed()) {
            <p class="blog-newsletter-widget__done" role="status">
              You're on the list — deals are on the way!
            </p>
          } @else {
            <form
              class="blog-newsletter-widget__form"
              [formGroup]="signup.form"
              (ngSubmit)="signup.submit('blog-cta')"
              novalidate
            >
              <div class="sav-honeypot" aria-hidden="true">
                <label
                  >Company website<input
                    formControlName="companyWebsite"
                    type="text"
                    tabindex="-1"
                    autocomplete="off"
                /></label>
              </div>
              <!-- Field and button share one rounded shell, the same fused-pill control the footer
                   signup uses, so the article's form is recognisably the site's newsletter. -->
              <div class="blog-newsletter-widget__field">
                <input
                  formControlName="email"
                  type="email"
                  placeholder="Your Email Address"
                  autocomplete="email"
                />
                <button type="submit" [disabled]="signup.submitting()">
                  {{ signup.submitting() ? '…' : 'Subscribe Now' }}
                </button>
              </div>
              <!-- Outside the pill: as a third flex item it used to squeeze the field and button
                   sideways the moment a validation message appeared. -->
              @if (signup.showEmailError()) {
                <span class="blog-newsletter-widget__error">Enter a valid email address.</span>
              } @else if (signup.error()) {
                <span class="blog-newsletter-widget__error">{{ signup.error() }}</span>
              }
            </form>
          }
        </div>
      </div>

      <!-- Comments are live data from `GET /blogs/:slug/comments` (approved only) — they were two
           invented readers hardcoded in the component. Avatars are CSS initials rather than
           ui-avatars.com images: that was one third-party request per comment and reply for what a
           circle and two letters render for free. -->
      <section class="sav-comments mb-12" aria-label="Comments">
        <app-section-heading
          [heading]="'Comments (' + commentCount() + ')'"
          subtitle="Join the discussion and share what worked for you."
        />

        @if (comments().length) {
          <ol class="sav-comment-list">
            @for (comment of comments(); track comment.id) {
              <li class="sav-comment">
                <span class="sav-comment-avatar" aria-hidden="true">{{
                  initials(comment.authorName)
                }}</span>
                <div class="sav-comment-main">
                  <div class="sav-comment-bubble">
                    <p class="sav-comment-author">
                      {{ comment.authorName }}
                      @if (comment.isStaffReply) {
                        <span class="sav-comment-badge">Team</span>
                      }
                    </p>
                    <p class="sav-comment-body">{{ comment.message }}</p>
                  </div>
                  <div class="sav-comment-meta">
                    <button
                      type="button"
                      class="sav-comment-link"
                      (click)="toggleReplyForm(comment.id)"
                    >
                      {{ replyingToCommentId() === comment.id ? 'Cancel' : 'Reply' }}
                    </button>
                    <time>{{ commentDate(comment.createdAt) }}</time>
                  </div>

                  @if (comment.replies.length) {
                    <ol class="sav-comment-replies">
                      @for (reply of comment.replies; track reply.id) {
                        <li class="sav-comment">
                          <span class="sav-comment-avatar" aria-hidden="true">{{
                            initials(reply.authorName)
                          }}</span>
                          <div class="sav-comment-main">
                            <div class="sav-comment-bubble">
                              <p class="sav-comment-author">
                                {{ reply.authorName }}
                                @if (reply.isStaffReply) {
                                  <span class="sav-comment-badge">Team</span>
                                }
                              </p>
                              <p class="sav-comment-body">{{ reply.message }}</p>
                            </div>
                            <div class="sav-comment-meta">
                              <time>{{ commentDate(reply.createdAt) }}</time>
                            </div>
                          </div>
                        </li>
                      }
                    </ol>
                  }

                  @if (replyingToCommentId() === comment.id) {
                    <div class="sav-comment-form sav-comment-form--reply">
                      <input #replyName type="text" placeholder="Your name" maxlength="80" />
                      <textarea
                        #replyMessage
                        rows="2"
                        [placeholder]="'Reply to ' + comment.authorName + '…'"
                        maxlength="2000"
                      ></textarea>
                      <button
                        type="button"
                        class="sav-comment-submit"
                        [disabled]="commentSubmitting()"
                        (click)="submitComment(replyName.value, replyMessage.value, comment.id)"
                      >
                        {{ commentSubmitting() ? 'Sending…' : 'Post reply' }}
                      </button>
                    </div>
                  }
                </div>
              </li>
            }
          </ol>
        } @else {
          <p class="sav-comment-empty">
            No comments yet — be the first to share what worked for you.
          </p>
        }

        <div class="sav-comment-form">
          <p class="sav-comment-form-title">Leave a comment</p>
          <input #commentName type="text" placeholder="Your name" maxlength="80" />
          <textarea
            #commentMessage
            rows="4"
            placeholder="Write your comment"
            maxlength="2000"
          ></textarea>

          @if (commentError()) {
            <p class="sav-comment-error" role="alert">{{ commentError() }}</p>
          } @else if (commentQueued()) {
            <p class="sav-comment-queued" role="status">
              Thanks — your comment has been sent for review and will appear once approved.
            </p>
          }

          <button
            type="button"
            class="sav-comment-submit"
            [disabled]="commentSubmitting()"
            (click)="submitComment(commentName.value, commentMessage.value)"
          >
            {{ commentSubmitting() ? 'Sending…' : 'Post comment' }}
          </button>
          <p class="sav-comment-note">
            Comments are reviewed before they appear. Links are not allowed.
          </p>
        </div>
      </section>
    </article>
  </main>

  <!-- The "{Event} Top Stores" row the home page leads with. This was a "Trending Stores" grid of
       its own, ranked by live offer count — unrelated to whatever campaign the site is actually
       running. It now reads the same featured event as the home page and the header, so changing
       the running event in admin moves this row with them. -->
  <app-event-top-stores />

  <section class="related-blogs-section mb-[50px]">
    <div class="sav-site-container">
      <app-section-heading
        heading="Related Blogs &amp; Deal Tips"
        subtitle="Expert coupon tips, shopping strategies and trending discount updates."
      />

      <!-- Real related articles, chosen server-side by silo, then category, then recency. This was
           four invented posts with Unsplash photography whose category and author links all 404'd. -->
      @if (relatedPosts().length) {
        <!-- The site's own blog listing grid rather than a one-off Tailwind ladder. This was
             1 / 2 / 4 with no three-column step, so a 1366px laptop jumped straight to four and the
             cards came out narrower than they are designed for. `.sav-blog-grid` is 4 / 3 / 2 / 1
             at 1500 / 1024 / 620, the same as every other blog listing. -->
        <div class="sav-blog-grid mb-20">
          @for (related of relatedPosts(); track related.id) {
            <article class="group">
              <a
                [routerLink]="'/blogs/' + related.slug"
                class="relative mb-8 block aspect-[5/4] overflow-hidden rounded-[2.5rem]"
                tabindex="-1"
                aria-hidden="true"
              >
                <img
                  [src]="related.imageUrl"
                  [alt]="related.title"
                  loading="lazy"
                  class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </a>
              <div
                class="mb-4 flex items-center gap-3 text-[10px] uppercase tracking-widest text-[var(--color-primary)] font-black"
              >
                <a
                  class="transition-colors hover:text-[var(--color-primary-deep)]"
                  [routerLink]="'/blogs/' + related.categorySlug"
                  >{{ related.category }}</a
                >
                <span class="h-1 w-1 rounded-full bg-slate-300"></span>
                <span class="text-slate-400">{{ related.readTime }}</span>
                <span class="h-1 w-1 rounded-full bg-slate-300"></span>
                <a
                  class="text-slate-500 transition-colors hover:text-[var(--color-primary-deep)]"
                  [routerLink]="'/experts/' + related.authorSlug"
                  >{{ related.author }}</a
                >
              </div>
              <h3 class="mb-4 transition-colors group-hover:text-[var(--color-primary)]">
                <a [routerLink]="'/blogs/' + related.slug">{{ related.title }}</a>
              </h3>
              <p class="line-clamp-3 text-[14px] leading-[1.6] text-slate-500 font-medium">
                {{ related.excerpt }}
              </p>
            </article>
          }
        </div>
      }
    </div>
  </section>

  <!-- Every published author, from the admin user list. Hidden when none are available rather
       than falling back to placeholder faces. -->
  @if (heroAuthors().length > 0) {
    <section aria-label="SavingArena authors" class="blog-cast-strip">
      <div class="blog-cast-container">
        <div class="blog-cast-head">
          <app-section-heading
            heading="SavingArena Heroes"
            subtitle="The editors and researchers behind our savings guides."
          />
          <div class="blog-cast-nav">
            <button
              type="button"
              class="sav-hero-arrow blog-cast-arrow"
              aria-label="Previous authors"
              [disabled]="!canScrollHeroesPrev()"
              (click)="scrollHeroes(-1)"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M15 5l-7 7 7 7"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              class="sav-hero-arrow blog-cast-arrow"
              aria-label="Next authors"
              [disabled]="!canScrollHeroesNext()"
              (click)="scrollHeroes(1)"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 5l7 7-7 7"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
        <div class="blog-cast-row" #heroesTrack (scroll)="onHeroesScroll()">
          @for (author of heroAuthors(); track author.slug) {
            <a [routerLink]="author.profilePath" class="blog-cast-link">
              @if (author.imageUrl) {
                <img
                  [src]="author.imageUrl | assetUrl"
                  [alt]="author.name"
                  width="80"
                  height="80"
                  loading="lazy"
                  decoding="async"
                  class="blog-cast-photo"
                />
              } @else {
                <span class="blog-cast-photo blog-cast-initial" aria-hidden="true">{{
                  author.name.charAt(0)
                }}</span>
              }
              <h3 class="blog-cast-name">{{ author.name }}</h3>
              <p class="blog-cast-role">{{ author.role }}</p>
            </a>
          }
        </div>
      </div>
    </section>
  }

  <!-- Alternative stores an admin assigned to this post. Hidden when none are assigned, rather
       than filling the row with unrelated brands. -->
  @if (relatedStores().length > 0) {
    <section aria-label="Alternative stores" class="zaful-competitors">
      <div class="sav-site-container">
        <app-section-heading
          heading="Stores Featured in This Guide"
          subtitle="Brands mentioned above, with their current offers."
        />
        <div class="zaful-competitor-grid">
          @for (store of relatedStores(); track store.id) {
            <a [routerLink]="store.path" class="zaful-competitor-card">
              @if (store.logoUrl) {
                <img
                  [src]="store.logoUrl | assetUrl"
                  [alt]="store.name + ' logo'"
                  width="112"
                  height="112"
                />
              }
              <div class="zaful-competitor-content">
                <h3>{{ store.name }}</h3>
                @if (store.domain) {
                  <p class="zaful-competitor-domain">{{ store.domain }}</p>
                }
                @if (store.description) {
                  <p>{{ store.description }}</p>
                }
              </div>
            </a>
          }
        </div>
      </div>
    </section>
  }
</div>

